import React, { useState, useEffect } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { toast } from 'react-toastify';
import { ELEMENT_CATEGORIES, ELEMENT_LABELS, ELEMENT_DEFAULT_SIZES, DEFAULT_ELEMENT_TYPE } from '../../utils/layoutElementTypes';

const ElementPropertiesModal = ({ isOpen, element, zoneId, savedColors, onClose, onSaved, onDeleted }) => {
  const [type, setType] = useState(DEFAULT_ELEMENT_TYPE);
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEdit = !!element;

  useEffect(() => {
    if (!isOpen) return;
    if (element) {
      setType(element.type || DEFAULT_ELEMENT_TYPE);
      setLabel(element.label || '');
      setColor(element.color || '');
    } else {
      setType(DEFAULT_ELEMENT_TYPE);
      setLabel('');
      setColor('');
    }
  }, [isOpen, element]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const res = await callAPI(
          apiUrls.FLOOR_PLAN_UPDATE_ELEMENT,
          {},
          'POST',
          { id: element.id, type, label: label || null, color: color || null },
          {}
        );
        onSaved(res.data.data, false);
        toast.success('Element updated');
      } else {
        const size = ELEMENT_DEFAULT_SIZES[type] || { width: 40, height: 40 };
        const res = await callAPI(
          apiUrls.FLOOR_PLAN_CREATE_ELEMENT,
          {},
          'POST',
          {
            zone_id: zoneId,
            type,
            label: label || null,
            color: color || null,
            width: size.width,
            height: size.height,
            position_x: 20,
            position_y: 20,
          },
          {}
        );
        onSaved(res.data.data, true);
        toast.success('Element created');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save element');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setDeleting(true);
    try {
      await callAPI(apiUrls.FLOOR_PLAN_DELETE_ELEMENT, {}, 'POST', { id: element.id }, {});
      toast.success('Element deleted');
      onDeleted(element.id);
    } catch (err) {
      toast.error('Failed to delete element');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="elementPropertiesModal">
      <div className="modal-header">
        <h5 className="modal-title">{isEdit ? 'Edit Element' : 'Add Element'}</h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>

      <div className="modal-body">
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select className="form-control" value={type} onChange={(e) => setType(e.target.value)}>
            {ELEMENT_CATEGORIES.map((group) => (
              <optgroup key={group.category} label={group.category}>
                {group.options.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Custom Label (optional)</label>
          <input
            type="text"
            className="form-control"
            placeholder={ELEMENT_LABELS[type]}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Color</label>
          <input
            type="color"
            className="form-control form-control-color"
            value={color || '#8a8a8a'}
            onChange={(e) => setColor(e.target.value)}
          />
          {savedColors && savedColors.length > 0 && (
            <div className="savedColorSwatches">
              {savedColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`colorSwatch${color === c ? ' selected' : ''}`}
                  style={{ backgroundColor: c }}
                  title={c}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer justify-content-center">
        {isEdit && (
          <button className="btn btn-danger" onClick={handleDelete} disabled={deleting || saving}>
            {deleting ? 'Deleting...' : 'Delete Element'}
          </button>
        )}
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btnPrimary" onClick={handleSave} disabled={saving || deleting}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </CommonModal>
  );
};

export default ElementPropertiesModal;
